import apiClient from "./axios";
import { ApiResponse } from "../types";

export interface FeeConfigItem {
  id: string;
  feeKey: string;
  amount: number;
  description: string;
  updatedBy: string | null;
  updatedAt: string;
}

export interface TierLimit {
  id: string;
  tier: string;
  dailyTransactionLimit: number;
  monthlyTransactionLimit: number;
  balanceLimit: number;
  singleTransactionLimit: number;
  usdDailyTransactionLimit: number;
  usdMonthlyTransactionLimit: number;
  usdSingleTransactionLimit: number;
  /** Retired: Ibra chat is allowed by characters now (`ibraCharsPerDay`). */
  aiPromptsPerDay: number;
  /** Chatting with Ibra, in characters a day. Null uses the tier's default. */
  ibraCharsPerDay: number | null;
  /** Talking to Ibra, in characters a day. Null uses the tier's default. */
  voiceCharsPerDay: number | null;
  updatedAt: string;
}

/**
 * What a business may move at each KYB tier.
 *
 * A separate ladder from the consumer one, and a separate table behind it:
 * the numbers answer a different regulatory question, and overloading one
 * would mean a change to consumer limits silently repricing every business
 * account.
 *
 * Null means uncapped. Zero means "may not transfer at all" — a different
 * statement, and conflating the two is how an uncapped tier ends up blocking
 * every payment.
 */
export interface BusinessTierLimit {
  id: string;
  tier: 1 | 2 | 3;
  name: string;
  singleTransferLimit: number | null;
  dailyLimit: number | null;
  cardLimit: number | null;
  internationalEnabled: boolean;
  isActive: boolean;
}

export interface UpdateBusinessTierLimitPayload {
  singleTransferLimit?: number | null;
  dailyLimit?: number | null;
  cardLimit?: number | null;
  internationalEnabled?: boolean;
}

export interface UpdateTierLimitPayload {
  dailyTransactionLimit: number;
  monthlyTransactionLimit: number;
  balanceLimit: number;
  singleTransactionLimit: number;
  usdDailyTransactionLimit: number;
  usdMonthlyTransactionLimit: number;
  usdSingleTransactionLimit: number;
  ibraCharsPerDay: number;
  voiceCharsPerDay: number;
}

export interface SystemConfig {
  bankingProvider: string;
  cardProvider: string;
  cardNgnProvider: string;
  cardUsdProvider: string;
  maxLoginAttempts: number;
  loginLockoutDurationMinutes: number;
  aiModel: string;
  aiMaxContextMessages: number;
}

export interface PremiumConfig {
  monthlyFee: number;
  trialDays: number;
}

/** What paying back credit fees has cost since the 1st of this month. */
export interface CreditFeeCoverSpend {
  since: string;
  count: number;
  consumer: number;
  business: number;
  /** Refunds the bank has not confirmed yet. */
  pending: number;
  /** Refunds the bank kept refusing, to be moved by hand. */
  failed: number;
  failedAmount: number;
}

/** Which provider answers BVN and NIN first. */
export interface IdentityProviderStatus {
  primary: "interswitch" | "dojah";
  interswitchConfigured: boolean;
}

export const configApi = {
  getFeeConfigs: async () => {
    const { data } =
      await apiClient.get<ApiResponse<FeeConfigItem[]>>("/admin/config/fees");
    return data.data;
  },

  updateTransferFee: async (markupAmount: number) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem>>(
      "/admin/config/fees/transfer",
      { markupAmount },
    );
    return data.data;
  },

  /** PayIQ-to-PayIQ (Safe Haven) transfers. 0 keeps them free. */
  updateIntraBankTransferFee: async (markupAmount: number) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem>>(
      "/admin/config/fees/transfer-intrabank",
      { markupAmount },
    );
    return data.data;
  },

  getFxConfig: async () => {
    const { data } = await apiClient.get<
      ApiResponse<{
        id: string;
        baseNgnUsdRate: number;
        markupPercent: number;
        cardCreationFeeNgn: number;
        cardCreationFeeUsdCents: number;
        cardPhysicalCreationFeeNgn: number;
        /** Absent until the backend that adds it is deployed. */
        businessCardCreationFeeNgn?: number;
      }>
    >("/admin/config/fx");
    return data.data;
  },

  updateFxConfig: async (baseNgnUsdRate: number, markupPercent: number) => {
    const { data } = await apiClient.patch<ApiResponse<{ message: string }>>(
      "/admin/config/fx",
      { baseNgnUsdRate, markupPercent },
    );
    return data.data;
  },

  /** Send only the fees being changed — the backend leaves the rest alone. */
  updateCardFees: async (fees: {
    cardCreationFeeNgn?: number;
    cardCreationFeeUsdCents?: number;
    cardPhysicalCreationFeeNgn?: number;
    businessCardCreationFeeNgn?: number;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<{ message: string }>>(
      "/admin/config/fees/cards",
      fees,
    );
    return data.data;
  },

  updateShopFees: async (fees: {
    orderTransactionFee?: number;
    cancellationFee?: number;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/shop",
      fees,
    );
    return data.data;
  },

  /**
   * What one automatic WhatsApp reminder costs a business past its plan's
   * monthly allowance, in naira. Meta reprices conversations per country and
   * category, so this is a price that moves without a deploy.
   */
  updateWhatsappReminderFee: async (amount: number) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem>>(
      "/admin/config/fees/whatsapp-reminder",
      { amount },
    );
    return data.data;
  },

  updatePayoutFee: async (fees: { percent?: number; cap?: number }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/payout",
      fees,
    );
    return data.data;
  },

  updateAirtimeDiscount: async (cfg: {
    discountPercent?: number;
    passDiscountToUser?: boolean;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/airtime-discount",
      cfg,
    );
    return data.data;
  },

  /** Safe Haven's commission per kind of purchase, percent of face value. */
  updateVasCommission: async (rates: {
    airtime?: number;
    data?: number;
    cable?: number;
    electricity?: number;
    /** Collect commission at all. */
    enabled?: boolean;
    /** Per electricity company, by key. */
    discos?: Record<string, number>;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/vas-commission",
      rates,
    );
    return data.data;
  },

  /** Free transfers a month, for people and for businesses. 0 is off. */
  updateCreditFeeCover: async (cfg: {
    enabled?: boolean;
    consumerPercent?: number;
    businessPercent?: number;
    coverStampDuty?: boolean;
    maxCharge?: number;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/credit-fee-cover",
      cfg,
    );
    return data.data;
  },

  catchUpCreditFeeCover: async () => {
    const { data } = await apiClient.post<
      ApiResponse<{ covered: number; amount: number }>
    >("/admin/config/fees/credit-fee-cover/catch-up");
    return data.data;
  },

  getCreditFeeCoverSpend: async () => {
    const { data } = await apiClient.get<ApiResponse<CreditFeeCoverSpend>>(
      "/admin/config/fees/credit-fee-cover/spend",
    );
    return data.data;
  },

  updateFreeTransfers: async (cfg: {
    consumerMonthly?: number;
    businessMonthly?: number;
  }) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem[]>>(
      "/admin/config/fees/free-transfers",
      cfg,
    );
    return data.data;
  },

  getBusinessTierLimits: async () => {
    const { data } = await apiClient.get<ApiResponse<BusinessTierLimit[]>>(
      "/admin/config/business-tier-limits",
    );
    return data.data;
  },

  updateBusinessTierLimit: async (
    tier: number,
    updates: UpdateBusinessTierLimitPayload,
  ) => {
    const { data } = await apiClient.patch<ApiResponse<BusinessTierLimit>>(
      `/admin/config/business-tier-limits/${tier}`,
      updates,
    );
    return data.data;
  },

  getTierLimits: async () => {
    const { data } = await apiClient.get<ApiResponse<TierLimit[]>>(
      "/admin/config/tier-limits",
    );
    return data.data;
  },

  updateTierLimit: async (
    tier: string,
    updates: UpdateTierLimitPayload,
  ) => {
    const { data } = await apiClient.patch<ApiResponse<TierLimit>>(
      `/admin/config/tier-limits/${tier}`,
      updates,
    );
    return data.data;
  },

  getSystemConfig: async () => {
    const { data } = await apiClient.get<ApiResponse<SystemConfig>>(
      "/admin/config/system",
    );
    return data.data;
  },

  getIdentityProvider: async () => {
    const { data } = await apiClient.get<ApiResponse<IdentityProviderStatus>>(
      "/admin/config/system/identity-provider",
    );
    return data.data;
  },

  updateIdentityProvider: async (primary: "interswitch" | "dojah") => {
    const { data } = await apiClient.patch<ApiResponse<IdentityProviderStatus>>(
      "/admin/config/system/identity-provider",
      { primary },
    );
    return data.data;
  },

  updateBankingProvider: async (provider: string) => {
    const { data } = await apiClient.patch<ApiResponse<{ message: string }>>(
      "/admin/config/banking-provider",
      { provider },
    );
    return data.data;
  },

  updateCardProvider: async (provider: string, type: string) => {
    const { data } = await apiClient.patch<ApiResponse<{ message: string }>>(
      "/admin/config/card-provider",
      { provider, type },
    );
    return data.data;
  },

  getPremiumConfig: async () => {
    const { data } = await apiClient.get<ApiResponse<PremiumConfig>>(
      "/admin/config/premium",
    );
    return data.data;
  },

  updatePremiumFee: async (amount: number) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem>>(
      "/admin/config/premium/fee",
      { amount },
    );
    return data.data;
  },

  updatePremiumTrial: async (trialDays: number) => {
    const { data } = await apiClient.patch<ApiResponse<FeeConfigItem>>(
      "/admin/config/premium/trial",
      { trialDays },
    );
    return data.data;
  },
};
